import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchAnalyticsSummary } from "../../analytics";
import "./AnalyticsDashboard.css";

const AnalyticsDashboard = () => {
  const [period, setPeriod] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [discordStatus, setDiscordStatus] = useState(null);

  const loadData = async (selectedPeriod = period, sendDiscord = false) => {
    setLoading(true);
    setError(null);
    try {
      const summary = await fetchAnalyticsSummary(selectedPeriod, sendDiscord);
      setData(summary);
      if (sendDiscord) {
        setDiscordStatus("✅ Samenvatting succesvol naar Discord verstuurd!");
        setTimeout(() => setDiscordStatus(null), 4000);
      }
    } catch (err) {
      console.warn("Analytics fetch error:", err);
      setError(err.message || "Kon analytics niet ophalen. Draait de backend container?");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(period);
  }, [period]);

  const handleSendDiscord = () => {
    loadData(period, true);
  };

  // Bereken percentages voor top clicks
  const maxClicks = data?.topTargets?.[0]?.count || 1;

  return (
    <div className="analytics-page">
      <div className="analytics-container">
        {/* Header */}
        <header className="analytics-header">
          <div className="analytics-header__left">
            <Link to="/" className="analytics-back-btn">
              ← Terug naar portfolio
            </Link>
            <h1 className="analytics-title">📊 Portfolio Analytics & Stats</h1>
            <p className="analytics-subtitle">
              Inzicht in bezoekers, interactie en waar mensen op klikken
            </p>
          </div>

          <div className="analytics-header__actions">
            <div className="analytics-period-select">
              <button
                className={`period-btn ${period === "7d" ? "active" : ""}`}
                onClick={() => setPeriod("7d")}
              >
                7 Dagen
              </button>
              <button
                className={`period-btn ${period === "30d" ? "active" : ""}`}
                onClick={() => setPeriod("30d")}
              >
                30 Dagen
              </button>
              <button
                className={`period-btn ${period === "month" ? "active" : ""}`}
                onClick={() => setPeriod("month")}
              >
                Deze maand
              </button>
            </div>

            <button
              className="analytics-btn analytics-btn--discord"
              onClick={handleSendDiscord}
              title="Stuur samenvatting naar je Discord Webhook"
              disabled={loading}
            >
              <i className="fab fa-discord"></i> Naar Discord
            </button>

            <button
              className="analytics-btn analytics-btn--refresh"
              onClick={() => loadData(period)}
              title="Vernieuwen"
              disabled={loading}
            >
              <i className={`icon-refresh ${loading ? "spinning" : ""}`}></i>
            </button>
          </div>
        </header>

        {discordStatus && (
          <div className="analytics-alert analytics-alert--success">
            {discordStatus}
          </div>
        )}

        {/* Error / Offline state */}
        {error && (
          <div className="analytics-alert analytics-alert--warning">
            <div className="alert-content">
              <strong>⚠️ Backend / Database Offline:</strong> {error}
              <p className="alert-hint">
                Draait de backend Docker container op je Proxmox server?
                Start de backend en MongoDB container via:
                <code>docker compose -f deployment/docker-compose.yml up -d</code>
              </p>
            </div>
          </div>
        )}

        {/* KPI Cards */}
        <div className="analytics-grid-cards">
          <div className="analytics-card kpi-card">
            <div className="kpi-icon kpi-icon--purple">
              <i className="icon-eye"></i>
            </div>
            <div className="kpi-data">
              <span className="kpi-label">Paginaweergaven</span>
              <h2 className="kpi-value">{loading ? "-" : data?.totalVisits ?? 0}</h2>
            </div>
          </div>

          <div className="analytics-card kpi-card">
            <div className="kpi-icon kpi-icon--blue">
              <i className="icon-user"></i>
            </div>
            <div className="kpi-data">
              <span className="kpi-label">Unieke Sessies</span>
              <h2 className="kpi-value">{loading ? "-" : data?.uniqueVisitors ?? 0}</h2>
            </div>
          </div>

          <div className="analytics-card kpi-card">
            <div className="kpi-icon kpi-icon--orange">
              <i className="icon-cursor"></i>
            </div>
            <div className="kpi-data">
              <span className="kpi-label">Totaal Aantal Kliks</span>
              <h2 className="kpi-value">{loading ? "-" : data?.totalClicks ?? 0}</h2>
            </div>
          </div>

          <div className="analytics-card kpi-card">
            <div className="kpi-icon kpi-icon--green">
              <i className="icon-energy"></i>
            </div>
            <div className="kpi-data">
              <span className="kpi-label">Totaal Events</span>
              <h2 className="kpi-value">{loading ? "-" : data?.totalEvents ?? 0}</h2>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="analytics-sections-grid">
          {/* Top Clicks */}
          <section className="analytics-card analytics-table-card">
            <div className="card-header">
              <h3>🎯 Waar klikken mensen op? (Top Clicks)</h3>
              <span className="badge">
                {data?.topTargets?.length || 0} acties gemeten
              </span>
            </div>

            {loading ? (
              <p className="state-msg">Data laden...</p>
            ) : !data?.topTargets || data.topTargets.length === 0 ? (
              <p className="state-msg state-msg--empty">
                Nog geen klik-events vastgelegd in deze periode. Klik eens rond op de website om data te zien!
              </p>
            ) : (
              <div className="analytics-list">
                {data.topTargets.map((item, index) => {
                  const targetText =
                    item?._id?.text ||
                    item?._id?.href ||
                    item?._id?.tag ||
                    "(Geen label)";
                  const href = item?._id?.href || "";
                  const count = item.count;
                  const percent = Math.round((count / maxClicks) * 100);

                  return (
                    <div className="analytics-list-item" key={index}>
                      <div className="item-rank">#{index + 1}</div>
                      <div className="item-details">
                        <div className="item-main">
                          <span className="item-title">{targetText}</span>
                          {href && <span className="item-href">{href}</span>}
                        </div>
                        <div className="item-bar-wrapper">
                          <div
                            className="item-bar-fill"
                            style={{ width: `${percent}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="item-count">
                        <strong>{count}</strong>
                        <span>kliks</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Top Pages */}
          <section className="analytics-card analytics-table-card">
            <div className="card-header">
              <h3>📄 Meest Bezochte Pagina's</h3>
              <span className="badge">
                {data?.topPaths?.length || 0} pagina's
              </span>
            </div>

            {loading ? (
              <p className="state-msg">Data laden...</p>
            ) : !data?.topPaths || data.topPaths.length === 0 ? (
              <p className="state-msg state-msg--empty">
                Nog geen page views vastgelegd in deze periode.
              </p>
            ) : (
              <div className="analytics-list">
                {data.topPaths.map((item, index) => (
                  <div className="analytics-list-item" key={index}>
                    <div className="item-rank">#{index + 1}</div>
                    <div className="item-details">
                      <div className="item-main">
                        <span className="item-title">{item._id || "/"}</span>
                      </div>
                    </div>
                    <div className="item-count">
                      <strong>{item.count}</strong>
                      <span>views</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Daily Clicks Timeline */}
        {data?.dailyClicks && data.dailyClicks.length > 0 && (
          <section className="analytics-card analytics-timeline-card">
            <div className="card-header">
              <h3>📅 Klik-activiteit per dag</h3>
            </div>
            <div className="daily-clicks-list">
              {data.dailyClicks.map((dayItem, i) => (
                <div key={i} className="daily-click-col">
                  <div className="daily-click-bar-wrap">
                    <div
                      className="daily-click-bar"
                      style={{
                        height: `${Math.min(100, (dayItem.clicks / (maxClicks || 1)) * 100)}%`
                      }}
                      title={`${dayItem.clicks} kliks op ${dayItem._id.day}`}
                    ></div>
                  </div>
                  <span className="daily-click-count">{dayItem.clicks}</span>
                  <span className="daily-click-date">
                    {dayItem._id.day?.slice(5)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Info card for Docker & LXC */}
        <section className="analytics-card analytics-info-card">
          <h4>💡 Hoe werkt deze tracking?</h4>
          <p>
            Elke klik op knoppen, links, CV downloads, categorie filters en terminal-commando's wordt
            anoniem via een sessie-ID geregistreerd. De data wordt lokaal opgeslagen in jouw eigen
            MongoDB database en kan met één klik naar je Discord webhook worden doorgestuurd.
          </p>
        </section>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
